import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState } from "@/components/Common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScanLine } from "lucide-react";
import { Registrazione } from "@/components/accessi/Registrazione";
import { EventiAccessi } from "@/components/accessi/EventiAccessi";
import { Affiliazioni } from "@/components/accessi/Affiliazioni";
import { StoricoAccessi } from "@/components/accessi/StoricoAccessi";
import { ReportPresenze } from "@/components/accessi/ReportPresenze";
import { ImpostazioniAccessi } from "@/components/accessi/ImpostazioniAccessi";
import { CruscottoPresenze } from "@/components/accessi/CruscottoPresenze";

const Accessi = () => {
  const { can } = useAuth();
  const [config, setConfig] = useState(null);
  const [eventi, setEventi] = useState([]);
  const [evento, setEvento] = useState(null);
  const [tab, setTab] = useState("registrazione");
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    try { const { data } = await api.get("/accessi/impostazioni"); setConfig(data); } catch (e) { console.error(e); }
  }, []);

  const loadEventi = useCallback(async () => {
    try {
      const { data } = await api.get("/accessi/eventi");
      setEventi(data.items || []);
      const savedId = localStorage.getItem("accessi_evento_id");
      setEvento((prev) => {
        if (prev) { const f = (data.items || []).find((e) => e.id === prev.id); return f || prev; }
        if (savedId) { return (data.items || []).find((e) => e.id === savedId) || null; }
        return null;
      });
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadConfig(), loadEventi()]); setLoading(false); })();
  }, [loadConfig, loadEventi]);

  const selectEvento = (ev) => {
    setEvento(ev);
    if (ev) localStorage.setItem("accessi_evento_id", ev.id); else localStorage.removeItem("accessi_evento_id");
  };

  const refreshEvento = useCallback(async (conteggi) => {
    if (!evento) return;
    if (conteggi) { setEvento((prev) => (prev ? { ...prev, conteggi } : prev)); return; }
    try { const { data } = await api.get(`/accessi/eventi/${evento.id}`); setEvento(data); } catch (e) { console.error(e); }
  }, [evento]);

  const gruppi = config?.gruppi || [];

  const tabDefs = [
    { v: "registrazione", label: "Registrazione", show: can("accessi.register") },
    { v: "eventi", label: "Eventi e sessioni", show: can("accessi.register") },
    { v: "affiliazioni", label: "Affiliazioni", show: can("accessi.manage") },
    { v: "storico", label: "Storico accessi", show: can("accessi.register") },
    { v: "cruscotto", label: "Cruscotto", show: can("accessi.register") },
    { v: "report", label: "Report presenze", show: can("accessi.report") },
    { v: "impostazioni", label: "Impostazioni", show: can("accessi.manage") },
  ].filter((t) => t.show);

  return (
    <div data-testid="accessi-page">
      <PageHeader icon={ScanLine} title="Gestione Accessi"
        description="Riconoscimento associati, verifica affiliazione e registrazione presenze" />
      {loading ? <LoadingState /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto" data-testid="accessi-tabs">
            {tabDefs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`accessi-tab-${t.v}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>

          {can("accessi.register") && (
            <TabsContent value="registrazione" className="mt-4">
              <Registrazione evento={evento} gruppi={gruppi} onRefreshEvento={refreshEvento}
                goToEventi={() => setTab("eventi")} />
            </TabsContent>
          )}
          {can("accessi.register") && (
            <TabsContent value="eventi" className="mt-4">
              <EventiAccessi eventi={eventi} gruppi={gruppi} annoCorrente={config?.anno_corrente}
                current={evento} onSelect={(ev) => { selectEvento(ev); setTab("registrazione"); }}
                reload={loadEventi} />
            </TabsContent>
          )}
          {can("accessi.manage") && (
            <TabsContent value="affiliazioni" className="mt-4">
              <Affiliazioni gruppi={gruppi} annoCorrente={config?.anno_corrente} eventi={eventi} />
            </TabsContent>
          )}
          {can("accessi.register") && (
            <TabsContent value="storico" className="mt-4">
              <StoricoAccessi eventi={eventi} gruppi={gruppi} />
            </TabsContent>
          )}
          {can("accessi.register") && (
            <TabsContent value="cruscotto" className="mt-4">
              <CruscottoPresenze />
            </TabsContent>
          )}
          {can("accessi.report") && (
            <TabsContent value="report" className="mt-4">
              <ReportPresenze eventi={eventi} />
            </TabsContent>
          )}
          {can("accessi.manage") && (
            <TabsContent value="impostazioni" className="mt-4">
              <ImpostazioniAccessi config={config} onSaved={loadConfig} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};

export default Accessi;
